import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  notificationTemplateCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type { NotificationTemplateDto } from "@/features/notification/api/dto";
import type {
  NotificationChannel,
  NotificationLanguage,
} from "@/features/shared/api/enums";

interface ToggleArgs {
  code: string;
  channel: NotificationChannel;
  language: NotificationLanguage;
  isActive: boolean;
}

/**
 * Active/désactive un modèle
 * (`POST /NotificationTemplates/{code}/{channel}/{language}/active?isActive=...`).
 * Attention : `isActive` est un booléen NON NULLABLE en query — l'omettre
 * équivaut à envoyer `false` et désactive le modèle (guide §6.7).
 * Rôles : flashpay-superadmin, flashpay-admin.
 */
export const useToggleNotificationTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "templates", "toggle"],
    mutationFn: ({ code, channel, language, isActive }: ToggleArgs) =>
      mutateData({
        url: `${notificationEndpoints.templates.active(
          code,
          channel,
          language
        )}?isActive=${isActive}`,
        method: "POST",
      }) as Promise<NotificationTemplateDto>,
    onSuccess: (_data, variables) => {
      return invalidateAfterMutation(
        queryClient,
        notificationTemplateCacheKeys(
          variables.code,
          variables.channel,
          variables.language
        )
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useToggleNotificationTemplate failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
